import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  useWindowDimensions,
  ActivityIndicator,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle as SvgCircle } from 'react-native-svg';
import { useStrategyOrganizationsQuery } from '../../hooks/queries';
import { OrganizationList } from '../../types';
import { COLORS, FONTS } from '../../constants/config';
import { LoadingScreen, PageContainer, Navbar, Breadcrumb } from '../../components';
import { TableSkeleton } from '../../components/Skeleton';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useRoleNavigation, useAutoTranslate } from '../../hooks';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYear } from '../../contexts/YearContext';
import { formatPct } from '../../utils/formatPercentage';
import { commitmentColor, deviationColor } from '../../utils/metricColors';

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EyeIcon = ({ size = 18, color = '#64748B' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M2.06153 12.3474C1.97819 12.1229 1.97819 11.8759 2.06153 11.6514C2.87323 9.68324 4.25104 8.00042 6.0203 6.81628C7.78955 5.63214 9.87057 5 11.9995 5C14.1285 5 16.2095 5.63214 17.9788 6.81628C19.748 8.00042 21.1258 9.68324 21.9375 11.6514C22.0209 11.8759 22.0209 12.1229 21.9375 12.3474C21.1258 14.3155 19.748 15.9983 17.9788 17.1825C16.2095 18.3666 14.1285 18.9988 11.9995 18.9988C9.87057 18.9988 7.78955 18.3666 6.0203 17.1825C4.25104 15.9983 2.87323 14.3155 2.06153 12.3474Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Progress Circle Component
// ============================================================

interface ProgressCircleProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  backgroundColor?: string;
  showLabel?: boolean;
  labelSize?: number;
  textColor?: string;
}

const ProgressCircle = ({
  percentage,
  size = 120,
  strokeWidth = 10,
  color = '#22C55E',
  backgroundColor,
  showLabel = true,
  labelSize = 24,
  textColor = '#303030',
}: ProgressCircleProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const safePercentage = Math.min(Math.max(percentage, 0), 100);
  const strokeDashoffset = circumference - (safePercentage / 100) * circumference;
  // Background ring defaults to a translucent (~20%) version of the
  // progress color so it matches the SegmentedGauge theme.
  const ringBgColor = backgroundColor ?? `${color}33`;

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size} style={{ transform: [{ rotate: '-90deg' }] }}>
        <SvgCircle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={ringBgColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <SvgCircle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={strokeDashoffset}
        />
      </Svg>
      {showLabel && (
        <Text style={[styles.progressLabel, { fontSize: labelSize, color: textColor }]}>
          %{formatPct(safePercentage)}
        </Text>
      )}
    </View>
  );
};

// ============================================================
// Segmented Gauge Component (target arc + actual pie wedge)
// ============================================================

interface SegmentedGaugeProps {
  actual: number;
  target: number;
  size?: number;
  showLabel?: boolean;
  strokeWidth?: number;
  showDot?: boolean;
  color?: string;
}

const SegmentedGauge = ({ actual, target, size = 120, showLabel = true, strokeWidth = 8, showDot = true, color = '#2CA02C' }: SegmentedGaugeProps) => {
  const ringBgColor = `${color}33`; // ~20% opacity
  const wedgeFillColor = `${color}73`; // ~45% opacity
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = radius * 2 * Math.PI;

  const actualPercentage = Math.min(Math.max(actual, 0), 100);
  const targetPercentage = Math.min(Math.max(target, 0), 100);
  const targetOffset = circumference - (targetPercentage / 100) * circumference;

  const dotAngleRad = -Math.PI / 2 + (targetPercentage / 100) * 2 * Math.PI;
  const dotX = center + radius * Math.cos(dotAngleRad);
  const dotY = center + radius * Math.sin(dotAngleRad);
  const dotRadius = Math.max(3, Math.round(strokeWidth * 0.6));

  const getActualWedgePath = () => {
    if (actualPercentage <= 0) return '';
    const startAngleRad = -Math.PI / 2;
    const endAngleRad = startAngleRad + (actualPercentage / 100) * 2 * Math.PI;
    const innerR = radius - strokeWidth / 2;
    const x1 = center + innerR * Math.cos(startAngleRad);
    const y1 = center + innerR * Math.sin(startAngleRad);
    const x2 = center + innerR * Math.cos(endAngleRad);
    const y2 = center + innerR * Math.sin(endAngleRad);
    const largeArc = actualPercentage > 50 ? 1 : 0;
    return `M ${center} ${center} L ${x1} ${y1} A ${innerR} ${innerR} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  };

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        <SvgCircle
          cx={center}
          cy={center}
          r={radius}
          stroke={ringBgColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {actualPercentage > 0 && (
          <Path
            d={getActualWedgePath()}
            fill={wedgeFillColor}
          />
        )}
        {targetPercentage > 0 && (
          <SvgCircle
            cx={center}
            cy={center}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={targetOffset}
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}
        {showDot && targetPercentage > 0 && targetPercentage < 100 && (
          <SvgCircle
            cx={dotX}
            cy={dotY}
            r={dotRadius}
            fill={color}
            stroke="#FCFCFC"
            strokeWidth={1.5}
          />
        )}
      </Svg>
      {showLabel && (
        <View style={{ position: 'absolute', alignItems: 'center', justifyContent: 'center', width: size, height: size }}>
          <Text
            numberOfLines={1}
            style={{ fontSize: Math.round(size / 5), fontFamily: FONTS.numbers, fontWeight: '500', color: '#414042' }}
          >
            % {formatPct(actual)}
          </Text>
        </View>
      )}
    </View>
  );
};

// ============================================================
// Types for Pending Data
// ============================================================

interface OrganizationPendingData {
  id: number;
  name: string;
  strategic_goals_count: number;
  pending_goals_count: number;
  initiatives_count: number;
  pending_initiatives_count: number;
  kpis_count: number;
}

// ============================================================
// Main Component
// ============================================================

export default function OrganizationsScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { width } = useWindowDimensions();
  const { navigateToHome, canSeePending } = useRoleNavigation();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const { selectedYear } = useYear();
  const { translate, translateOrg } = useAutoTranslate();

  // Theme colors for dark mode support
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    textSecondary: colors.textSecondary,
    backgroundAlt: colors.backgroundAlt,
    border: colors.border,
    iconDefault: colors.iconDefault,
    tableHeader: isDark ? '#1F1F1F' : '#F8FAFC',
    tableRowAlt: isDark ? '#1A1A1A' : '#FAFAFA',
    tableCellText: colors.textPrimary,
    progressBarBg: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(51, 65, 85, 0.1)',
    circleBg: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(51, 65, 85, 0.2)',
  };

  // React Query holds the data — key includes selectedYear, so year change +
  // focus both cache properly. Pending counts come from the same queryset,
  // so `pendingLoading` collapses into the query's loading state.
  const {
    data: organizations = [],
    isPending: loading,
    isRefetching: refreshing,
    refetch,
  } = useStrategyOrganizationsQuery(selectedYear);
  const pendingLoading = loading;

  // Pending counts are already returned by OrganizationListSerializer;
  // memoize the per-row projection so a parent re-render doesn't rebuild
  // the whole array (breaks memoization of downstream cards otherwise).
  const pendingData = useMemo<OrganizationPendingData[]>(
    () => organizations.map((org) => ({
      id: org.id,
      name: org.name,
      strategic_goals_count: org.strategic_goals_count ?? 0,
      pending_goals_count: org.pending_goals_count ?? 0,
      initiatives_count: org.initiatives_count ?? 0,
      pending_initiatives_count: org.pending_initiatives_count ?? 0,
      kpis_count: org.kpis_count ?? 0,
    })),
    [organizations],
  );

  const onRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleViewDetails = (orgId: number) => {
    // Jump into the org-employee-style dashboard for this org (/dashboard/:id)
    // instead of the legacy OrganizationDetail screen.
    navigation.navigate('DashboardStack' as any, {
      screen: 'Dashboard',
      params: { organizationId: orgId },
    });
  };

  // No more blocking LoadingScreen — frame + breadcrumb render instantly and
  // the table area shows a skeleton on first load. With placeholderData on
  // the query, revisits / year switches keep prior content visible instead.

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('breadcrumb.home') },
            { label: t('organizations.title') },
          ]}
        />

        {/* Page Title */}
        <Text style={[styles.pageTitle, { color: themeColors.textPrimary }]}>{t('organizations.title')}</Text>

        {/* First-load skeleton — only when there's no cached data yet. After
            that, placeholderData keeps the previous content visible during
            refetches, so this branch is bypassed on revisits. */}
        {loading && (
          <TableSkeleton rows={5} />
        )}

        {/* Section 1: الاعتمادات في الانتظار - Pending Approvals Table - Hidden for executive (view-only) users */}
        {canSeePending && (
        <View style={[styles.tableSection, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <Text style={[styles.sectionTitle, { color: themeColors.textPrimary }]}>{t('strategyDashboard.pendingApprovals')}</Text>

          {pendingLoading ? (
            <View style={styles.tableLoadingContainer}>
              <ActivityIndicator size="small" color="#A9211F" />
            </View>
          ) : (
            <View style={[styles.tableContainer, { borderColor: themeColors.cardBorder }]}>
              {/* Table Header - RTL order: first item = RIGHT side, last item = LEFT side */}
              <View style={[styles.tableHeader, { backgroundColor: themeColors.tableHeader, borderBottomColor: themeColors.cardBorder }]}>
                <View style={[styles.tableHeaderCell, styles.tableHeaderCellName]}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textMuted }]}>{t('organizations.organization')}</Text>
                </View>
                <View style={styles.tableHeaderCell}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textMuted }]}>{t('strategyDashboard.strategicGoals')}</Text>
                </View>
                <View style={styles.tableHeaderCellHighlight}>
                  <Text style={styles.tableHeaderTextHighlight}>{t('strategyDashboard.goalsNeedApprovalShort')}</Text>
                </View>
                <View style={styles.tableHeaderCell}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textMuted }]}>{t('strategyDashboard.strategicInitiatives')}</Text>
                </View>
                <View style={styles.tableHeaderCellHighlight}>
                  <Text style={styles.tableHeaderTextHighlight}>{t('strategyDashboard.initiativesNeedApprovalShort')}</Text>
                </View>
                <View style={styles.tableHeaderCell}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textMuted }]}>{t('kpis.kpisCount')}</Text>
                </View>
                <View style={styles.tableHeaderCell}>
                  <Text style={[styles.tableHeaderText, { color: themeColors.textMuted }]}></Text>
                </View>
              </View>

              {/* Table Rows - RTL order: first item = RIGHT side, last item = LEFT side */}
              {pendingData.map((org, index) => (
                <View
                  key={org.id}
                  style={[
                    styles.tableRow,
                    { borderBottomColor: themeColors.border },
                    index % 2 === 1 && { backgroundColor: themeColors.tableRowAlt }
                  ]}
                >
                  {/* Organization Name - First (RIGHT side in RTL) */}
                  <View style={[styles.tableCell, styles.tableCellName]}>
                    <Text style={[styles.tableCellTextName, { color: themeColors.textPrimary }]} numberOfLines={1}>{translateOrg(org.name)}</Text>
                  </View>
                  {/* Goals Count */}
                  <View style={styles.tableCell}>
                    <Text style={[styles.tableCellText, { color: themeColors.textPrimary }]}>{org.strategic_goals_count}</Text>
                  </View>
                  {/* Pending Goals */}
                  <View style={styles.tableCellBadge}>
                    <View style={[
                      styles.pendingBadge,
                      org.pending_goals_count > 0 && styles.pendingBadgeActive
                    ]}>
                      <Text style={[
                        styles.pendingBadgeText,
                        org.pending_goals_count > 0 && styles.pendingBadgeTextActive
                      ]}>
                        {org.pending_goals_count}
                      </Text>
                    </View>
                  </View>
                  {/* Initiatives Count */}
                  <View style={styles.tableCell}>
                    <Text style={[styles.tableCellText, { color: themeColors.textPrimary }]}>{org.initiatives_count}</Text>
                  </View>
                  {/* Pending Initiatives */}
                  <View style={styles.tableCellBadge}>
                    <View style={[
                      styles.pendingBadge,
                      org.pending_initiatives_count > 0 && styles.pendingBadgeActive
                    ]}>
                      <Text style={[
                        styles.pendingBadgeText,
                        org.pending_initiatives_count > 0 && styles.pendingBadgeTextActive
                      ]}>
                        {org.pending_initiatives_count}
                      </Text>
                    </View>
                  </View>
                  {/* KPIs Count */}
                  <View style={styles.tableCell}>
                    <Text style={[styles.tableCellText, { color: themeColors.textPrimary }]}>{org.kpis_count}</Text>
                  </View>
                  {/* Eye Icon - Last (LEFT side in RTL) */}
                  <TouchableOpacity
                    style={styles.tableCellEye}
                    onPress={() => handleViewDetails(org.id)}
                  >
                    <EyeIcon size={18} color={themeColors.iconDefault} />
                  </TouchableOpacity>
                </View>
              ))}

              {pendingData.length === 0 && (
                <View style={styles.emptyTableRow}>
                  <Text style={[styles.emptyTableText, { color: themeColors.textMuted }]}>{t('common.noData')}</Text>
                </View>
              )}
            </View>
          )}
        </View>
        )}

        {/* Section 2: أداء الجهات - Organization Performance Cards */}
        <View style={[styles.cardsSection, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <Text style={[styles.sectionTitle, { color: themeColors.textPrimary }]}>{t('strategyDashboard.organizationPerformance')}</Text>

          {organizations.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={[styles.emptyStateText, { color: themeColors.textMuted }]}>{t('common.noData')}</Text>
            </View>
          ) : (
            <View style={styles.cardsGrid}>
              {organizations.map((org) => (
                <View key={org.id} style={[styles.orgCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
                  {/* Organization Name */}
                  <Text style={[styles.orgCardName, { color: themeColors.textPrimary }]} numberOfLines={2}>{translateOrg(org.name)}</Text>

                  {/* Two Donut Charts Row */}
                  <View style={styles.orgCardChartsRow}>
                    {/* مؤشر انحراف الأداء - Performance Deviation
                        Computed as 100 - commitment_index so the deviation
                        donut on this card always equals the rest of the
                        commitment gauge inside that org's detail page. */}
                    {(() => {
                      const commitment = (org as any).commitment_index;
                      const deviation = commitment != null
                        ? Math.max(0, Math.min(100, 100 - Number(commitment)))
                        : null;
                      return (
                        <View style={styles.orgCardChartItem}>
                          {deviation != null ? (
                            // When deviation is exactly 0 (commitment is
                            // 100%), draw the whole ring in translucent
                            // green so the card visibly signals "no
                            // deviation" instead of an empty gray ring.
                            deviation === 0 ? (
                              <ProgressCircle
                                percentage={100}
                                size={64}
                                strokeWidth={12}
                                color="rgba(34, 197, 94, 0.35)"
                                backgroundColor="rgba(34, 197, 94, 0.35)"
                                showLabel={false}
                              />
                            ) : (
                              <ProgressCircle
                                percentage={deviation}
                                size={64}
                                strokeWidth={12}
                                color={deviationColor(deviation)}
                                showLabel={false}
                              />
                            )
                          ) : (
                            <View style={{ width: 64, height: 64, justifyContent: 'center', alignItems: 'center' }}>
                              <Text style={{ fontSize: 20, fontWeight: '700', color: themeColors.textPrimary }}>--</Text>
                            </View>
                          )}
                          <View style={styles.orgCardChartLabels}>
                            <Text style={[styles.orgCardChartLabel, { color: themeColors.textPrimary }]}>{t('strategyDashboard.performanceDeviationIndex')}</Text>
                            <Text style={[styles.orgCardChartValue, { color: themeColors.textSecondary }]}>{deviation != null ? `%${formatPct(deviation)}` : '--'}</Text>
                          </View>
                        </View>
                      );
                    })()}

                    {/* مؤشر تحقيق الرؤية - Vision Achievement (target + actual on chart) */}
                    <View style={styles.orgCardChartItem}>
                      <SegmentedGauge
                        actual={Number(((org.stage_actual_progress ?? 0) * 100).toFixed(2))}
                        target={Number(((org.stage_required_progress ?? 0) * 100).toFixed(2))}
                        size={64}
                        strokeWidth={12}
                        showLabel={false}
                        showDot={false}
                        color={commitmentColor(org.commitment_index ?? 0)}
                      />
                      <View style={styles.orgCardChartLabels}>
                        <Text style={[styles.orgCardChartLabel, { color: themeColors.textPrimary }]}>{t('strategyDashboard.visionAchievementIndex')}</Text>
                        <Text style={[styles.orgCardChartValue, { color: themeColors.textSecondary }]}>%{formatPct((org.stage_actual_progress ?? 0) * 100)}</Text>
                      </View>
                    </View>
                  </View>

                  {/* Progress Bars Section */}
                  <View style={styles.orgCardProgressSection}>
                    {/* الأهداف الاستراتيجية */}
                    <View style={styles.orgProgressItem}>
                      <View style={styles.orgProgressHeader}>
                        <Text style={[styles.orgProgressValue, { color: themeColors.textPrimary }]}>{org.strategic_goals_achievement ?? 0} %</Text>
                        <Text style={[styles.orgProgressLabel, { color: themeColors.textPrimary }]}>{t('strategyDashboard.strategicGoals')}</Text>
                      </View>
                      <View style={[styles.orgProgressBarBg, { backgroundColor: themeColors.progressBarBg }]}>
                        <View style={[styles.orgProgressBarFill, { width: `${org.strategic_goals_achievement ?? 0}%` }]} />
                      </View>
                    </View>

                    {/* المبادرات الاستراتيجية */}
                    <View style={styles.orgProgressItem}>
                      <View style={styles.orgProgressHeader}>
                        <Text style={[styles.orgProgressValue, { color: themeColors.textPrimary }]}>{org.initiatives_achievement ?? 0} %</Text>
                        <Text style={[styles.orgProgressLabel, { color: themeColors.textPrimary }]}>{t('strategyDashboard.strategicInitiatives')}</Text>
                      </View>
                      <View style={[styles.orgProgressBarBg, { backgroundColor: themeColors.progressBarBg }]}>
                        <View style={[styles.orgProgressBarFill, { width: `${org.initiatives_achievement ?? 0}%` }]} />
                      </View>
                    </View>

                    {/* العمليات */}
                    <View style={styles.orgProgressItem}>
                      <View style={styles.orgProgressHeader}>
                        <Text style={[styles.orgProgressValue, { color: themeColors.textPrimary }]}>{org.processes_achievement ?? 0} %</Text>
                        <Text style={[styles.orgProgressLabel, { color: themeColors.textPrimary }]}>{t('sidebar.processes')}</Text>
                      </View>
                      <View style={[styles.orgProgressBarBg, { backgroundColor: themeColors.progressBarBg }]}>
                        <View style={[styles.orgProgressBarFill, { width: `${org.processes_achievement ?? 0}%` }]} />
                      </View>
                    </View>

                    {/* الخدمات */}
                    <View style={styles.orgProgressItem}>
                      <View style={styles.orgProgressHeader}>
                        <Text style={[styles.orgProgressValue, { color: themeColors.textPrimary }]}>{org.services_achievement ?? 0} %</Text>
                        <Text style={[styles.orgProgressLabel, { color: themeColors.textPrimary }]}>{t('sidebar.services')}</Text>
                      </View>
                      <View style={[styles.orgProgressBarBg, { backgroundColor: themeColors.progressBarBg }]}>
                        <View style={[styles.orgProgressBarFill, { width: `${org.services_achievement ?? 0}%` }]} />
                      </View>
                    </View>

                    {/* الوحدات التنظيمية */}
                    <View style={styles.orgProgressItem}>
                      <View style={styles.orgProgressHeader}>
                        <Text style={[styles.orgProgressValue, { color: themeColors.textPrimary }]}>{org.units_achievement ?? 0} %</Text>
                        <Text style={[styles.orgProgressLabel, { color: themeColors.textPrimary }]}>{t('sidebar.organizationalUnits')}</Text>
                      </View>
                      <View style={[styles.orgProgressBarBg, { backgroundColor: themeColors.progressBarBg }]}>
                        <View style={[styles.orgProgressBarFill, { width: `${org.units_achievement ?? 0}%` }]} />
                      </View>
                    </View>

                    {/* رأس المال البشري */}
                    <View style={styles.orgProgressItem}>
                      <View style={styles.orgProgressHeader}>
                        <Text style={[styles.orgProgressValue, { color: themeColors.textPrimary }]}>{org.human_capital_achievement ?? 0} %</Text>
                        <Text style={[styles.orgProgressLabel, { color: themeColors.textPrimary }]}>{t('strategyDashboard.humanCapital')}</Text>
                      </View>
                      <View style={[styles.orgProgressBarBg, { backgroundColor: themeColors.progressBarBg }]}>
                        <View style={[styles.orgProgressBarFill, { width: `${org.human_capital_achievement ?? 0}%` }]} />
                      </View>
                    </View>
                  </View>

                  {/* View Details Link */}
                  <TouchableOpacity
                    style={styles.orgCardLink}
                    onPress={() => handleViewDetails(org.id)}
                  >
                    <ChevronLeftIcon size={16} color={themeColors.textPrimary} />
                    <Text style={[styles.orgCardLinkText, { color: themeColors.textPrimary }]}>{t('common.viewDetails')}</Text>
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          )}
        </View>
      </ScrollView>
    </PageContainer>
  );
}

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
    paddingBottom: 40,
  },

  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    marginBottom: 8,
  },
  breadcrumbLink: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#3B82F6',
  },
  breadcrumbSeparator: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    marginHorizontal: 8,
  },
  breadcrumbCurrent: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#A9211F',
  },

  // Page Title
  pageTitle: {
    fontSize: 24,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 24,
  },

  // Section Title
  sectionTitle: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 16,
  },

  // Table Section
  tableSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 20,
    marginBottom: 24,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  tableLoadingContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tableContainer: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    overflow: 'hidden',
  },

  // Table Header
  tableHeader: {
    flexDirection: 'row-reverse',
    backgroundColor: '#F8FAFC',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  tableHeaderCell: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tableHeaderCellName: {
    flex: 2,
    alignItems: 'flex-end',
  },
  tableHeaderCellHighlight: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tableHeaderText: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    color: '#64748B',
    textAlign: 'center',
  },
  tableHeaderTextHighlight: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    color: '#0891B2',
    textAlign: 'center',
  },

  // Table Row
  tableRow: {
    flexDirection: 'row-reverse',
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
    alignItems: 'center',
  },
  tableRowAlt: {
    backgroundColor: '#FAFAFA',
  },
  tableCell: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tableCellName: {
    flex: 2,
    alignItems: 'flex-end',
  },
  tableCellEye: {
    width: 50,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tableCellBadge: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tableCellText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'center',
  },
  tableCellTextName: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
  },

  // Pending Badge
  pendingBadge: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 4,
    minWidth: 36,
    alignItems: 'center',
  },
  pendingBadgeActive: {
    backgroundColor: '#E0F7FA',
  },
  pendingBadgeText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#64748B',
    textAlign: 'center',
  },
  pendingBadgeTextActive: {
    color: '#0891B2',
  },

  // Empty Table
  emptyTableRow: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTableText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },

  // Cards Section
  cardsSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 20,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  cardsGrid: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: 20,
    justifyContent: 'flex-start',
  },

  // Organization Card
  orgCard: {
    width: '31%',
    minWidth: 300,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 12,
    gap: 16,
  },
  orgCardName: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 24,
  },

  // Charts Row
  orgCardChartsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
  },
  orgCardChartItem: {
    flex: 1,
    alignItems: 'center',
    gap: 8,
  },
  orgCardChartLabels: {
    alignItems: 'center',
    gap: 2,
  },
  orgCardChartLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'center',
  },
  orgCardChartValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#303030',
  },

  // Progress Section
  orgCardProgressSection: {
    gap: 16,
  },
  orgProgressItem: {
    gap: 8,
  },
  orgProgressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  orgProgressLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    flex: 1,
  },
  orgProgressValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#414042',
  },
  orgProgressBarBg: {
    height: 8,
    backgroundColor: 'rgba(51, 65, 85, 0.1)',
    borderRadius: 8,
    overflow: 'hidden',
    width: '100%',
    flexDirection: 'row-reverse',
  },
  orgProgressBarFill: {
    height: '100%',
    backgroundColor: '#A9211F',
    borderRadius: 8,
  },

  // View Details Link
  orgCardLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 3,
    paddingHorizontal: 8,
  },
  orgCardLinkText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },

  // Empty State
  emptyState: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },

  // Progress Label
  progressLabel: {
    position: 'absolute',
    fontFamily: FONTS.medium,
  },
});
